<template>
  <div class="w-full max-w-md mx-auto">
    <div class="text-center mb-8">
      <h2 class="text-3xl font-bold text-gray-900">회원가입</h2>
      <p class="mt-2 text-gray-600">Seatisfy에 오신 것을 환영합니다</p>
    </div>

    <form @submit.prevent="handleSubmit" class="space-y-6">
      <!-- 이메일 -->
      <div>
        <label for="email" class="form-label">이메일 *</label>
        <input
          id="email"
          v-model="form.email"
          type="email"
          required
          autocomplete="email"
          class="form-input"
          :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.email }"
          placeholder="example@email.com"
        />
        <p v-if="errors.email" class="form-error">{{ errors.email }}</p>
      </div>

      <!-- 비밀번호 -->
      <div>
        <label for="password" class="form-label">비밀번호 *</label>
        <div class="relative">
          <input
            id="password"
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            required
            autocomplete="new-password"
            class="form-input pr-10"
            :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.password }"
            placeholder="8자 이상, 영문자와 숫자 포함"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
          >
            <EyeIcon v-if="!showPassword" class="w-5 h-5" />
            <EyeSlashIcon v-else class="w-5 h-5" />
          </button>
        </div>
        <p v-if="errors.password" class="form-error">{{ errors.password }}</p>
      </div>

      <!-- 비밀번호 확인 -->
      <div>
        <label for="passwordConfirm" class="form-label">비밀번호 확인 *</label>
        <div class="relative">
          <input
            id="passwordConfirm"
            v-model="form.passwordConfirm"
            :type="showPasswordConfirm ? 'text' : 'password'"
            required
            autocomplete="new-password"
            class="form-input pr-10"
            :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.passwordConfirm }"
            placeholder="비밀번호를 다시 입력하세요"
          />
          <button
            type="button"
            @click="showPasswordConfirm = !showPasswordConfirm"
            class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
          >
            <EyeIcon v-if="!showPasswordConfirm" class="w-5 h-5" />
            <EyeSlashIcon v-else class="w-5 h-5" />
          </button>
        </div>
        <p v-if="errors.passwordConfirm" class="form-error">{{ errors.passwordConfirm }}</p>
      </div>

      <!-- 이름 -->
      <div>
        <label for="name" class="form-label">이름 *</label>
        <input
          id="name"
          v-model="form.name"
          type="text"
          required
          autocomplete="name"
          class="form-input"
          :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.name }"
          placeholder="홍길동"
        />
        <p v-if="errors.name" class="form-error">{{ errors.name }}</p>
      </div>

      <!-- 전화번호 -->
      <div>
        <label for="phone" class="form-label">전화번호 *</label>
        <input
          id="phone"
          v-model="form.phone"
          type="tel"
          required
          autocomplete="tel"
          class="form-input"
          :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.phone }"
          placeholder="010-1234-5678"
        />
        <p v-if="errors.phone" class="form-error">{{ errors.phone }}</p>
        <p class="form-hint">'-'를 포함하여 입력해주세요</p>
      </div>

      <!-- 이용약관 동의 -->
      <div class="space-y-3">
        <div class="flex items-center">
          <input
            id="agreeTerms"
            v-model="form.agreeTerms"
            type="checkbox"
            required
            class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label for="agreeTerms" class="ml-2 text-sm text-gray-600">
            이용약관에 동의합니다 *
            <button
              type="button"
              @click="showTerms = true"
              class="text-primary-600 hover:text-primary-500 underline"
            >
              (보기)
            </button>
          </label>
        </div>
        <div class="flex items-center">
          <input
            id="agreePrivacy"
            v-model="form.agreePrivacy"
            type="checkbox"
            required
            class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label for="agreePrivacy" class="ml-2 text-sm text-gray-600">
            개인정보 처리방침에 동의합니다 *
            <button
              type="button"
              @click="showPrivacy = true"
              class="text-primary-600 hover:text-primary-500 underline"
            >
              (보기)
            </button>
          </label>
        </div>
        <div class="flex items-center">
          <input
            id="agreeMarketing"
            v-model="form.agreeMarketing"
            type="checkbox"
            class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label for="agreeMarketing" class="ml-2 text-sm text-gray-600">
            마케팅 정보 수신에 동의합니다 (선택)
          </label>
        </div>
      </div>

      <!-- 회원가입 버튼 -->
      <button
        type="submit"
        :disabled="isLoading || !isFormValid"
        class="btn btn-primary btn-lg w-full"
        :class="{ 'opacity-50 cursor-not-allowed': isLoading || !isFormValid }"
      >
        <span v-if="isLoading" class="flex items-center justify-center">
          <div class="spinner w-5 h-5 mr-2"></div>
          회원가입 중...
        </span>
        <span v-else>회원가입</span>
      </button>

      <!-- 일반적인 에러 메시지 -->
      <div v-if="errors.general" class="bg-red-50 border border-red-200 rounded-md p-3">
        <p class="text-red-600 text-sm">{{ errors.general }}</p>
      </div>
    </form>

    <!-- 로그인 링크 -->
    <div class="mt-6 text-center">
      <p class="text-gray-600">
        이미 계정이 있으신가요?
        <RouterLink
          to="/login"
          class="text-primary-600 hover:text-primary-500 font-medium"
        >
          로그인
        </RouterLink>
      </p>
    </div>

    <!-- 이용약관 모달 -->
    <div v-if="showTerms" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div class="flex justify-between items-center p-6 border-b">
          <h3 class="text-lg font-semibold">이용약관</h3>
          <button @click="showTerms = false" class="text-gray-400 hover:text-gray-600">
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>
        <div class="p-6 overflow-y-auto max-h-96">
          <div class="prose prose-sm max-w-none">
            <h4>제1조 (목적)</h4>
            <p>이 약관은 Seatisfy(이하 "회사")이 제공하는 좌석 예약 서비스(이하 "서비스")의 이용에 관한 조건 및 절차, 회사와 이용자의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.</p>
            
            <h4>제2조 (정의)</h4>
            <p>이 약관에서 사용하는 용어의 정의는 다음과 같습니다:</p>
            <ul>
              <li>"서비스"라 함은 회사가 제공하는 좌석 예약 및 관련 서비스를 의미합니다.</li>
              <li>"이용자"라 함은 회사의 서비스에 접속하여 이 약관에 따라 회사가 제공하는 서비스를 받는 회원을 말합니다.</li>
            </ul>
            
            <h4>제3조 (약관의 명시와 설명 및 개정)</h4>
            <p>회사는 이 약관의 내용과 상호 및 대표자 성명, 영업소 소재지 주소, 전화번호 등을 이용자가 쉽게 알 수 있도록 서비스 초기 화면에 게시합니다.</p>
          </div>
        </div>
        <div class="p-4 border-t">
          <button @click="showTerms = false" class="btn btn-primary btn-sm w-full">
            확인
          </button>
        </div>
      </div>
    </div>

    <!-- 개인정보 처리방침 모달 -->
    <div v-if="showPrivacy" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div class="flex justify-between items-center p-6 border-b">
          <h3 class="text-lg font-semibold">개인정보 처리방침</h3>
          <button @click="showPrivacy = false" class="text-gray-400 hover:text-gray-600">
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>
        <div class="p-6 overflow-y-auto max-h-96">
          <div class="prose prose-sm max-w-none">
            <h4>1. 개인정보의 처리 목적</h4>
            <p>Seatisfy(이하 "회사")은 다음의 목적을 위하여 개인정보를 처리합니다:</p>
            <ul>
              <li>회원가입 및 관리</li>
              <li>서비스 제공에 관한 계약 이행 및 서비스 제공에 따른 요금정산</li>
              <li>고충처리</li>
            </ul>
            
            <h4>2. 개인정보의 처리 및 보유 기간</h4>
            <p>회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.</p>
            
            <h4>3. 정보주체와 법정대리인의 권리·의무 및 그 행사방법</h4>
            <p>정보주체는 회사에 대해 언제든지 개인정보 열람·정정·삭제·처리정지 요구 등의 권리를 행사할 수 있습니다.</p>
          </div>
        </div>
        <div class="p-4 border-t">
          <button @click="showPrivacy = false" class="btn btn-primary w-full">
            확인
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

const showPassword = ref(false)
const showPasswordConfirm = ref(false)
const isLoading = computed(() => authStore.isLoading)
const showTerms = ref(false)
const showPrivacy = ref(false)

// 폼 데이터
const form = reactive({
  email: '',
  password: '',
  passwordConfirm: '',
  name: '',
  phone: '',
  agreeTerms: false,
  agreePrivacy: false,
  agreeMarketing: false
})

// 에러 상태
const errors = reactive({
  email: '',
  password: '',
  passwordConfirm: '',
  name: '',
  phone: '',
  general: ''
})

// 폼 유효성 검사
const isFormValid = computed(() => {
  return form.email.trim() && 
         form.password.trim() && 
         form.passwordConfirm.trim() && 
         form.name.trim() && 
         form.phone.trim() && 
         form.agreeTerms && 
         form.agreePrivacy &&
         form.password === form.passwordConfirm
})

// 에러 초기화
const clearErrors = () => {
  errors.email = ''
  errors.password = ''
  errors.passwordConfirm = ''
  errors.name = ''
  errors.phone = ''
  errors.general = ''
}

// 폼 유효성 검사
const validateForm = (): boolean => {
  clearErrors()
  let isValid = true

  // 이메일 검증
  if (!form.email) {
    errors.email = '이메일을 입력해주세요.'
    isValid = false
  } else if (!/\S+@\S+\.\S+/.test(form.email)) {
    errors.email = '올바른 이메일 형식을 입력해주세요.'
    isValid = false
  }

  // 비밀번호 검증
  if (!form.password) {
    errors.password = '비밀번호를 입력해주세요.'
    isValid = false
  } else if (form.password.length < 8) {
    errors.password = '비밀번호는 8자 이상이어야 합니다.'
    isValid = false
  } else if (!/^(?=.*[A-Za-z])(?=.*\d)/.test(form.password)) {
    errors.password = '비밀번호는 영문자와 숫자를 포함해야 합니다.'
    isValid = false
  }

  // 비밀번호 확인 검증
  if (!form.passwordConfirm) {
    errors.passwordConfirm = '비밀번호 확인을 입력해주세요.'
    isValid = false
  } else if (form.password !== form.passwordConfirm) {
    errors.passwordConfirm = '비밀번호가 일치하지 않습니다.'
    isValid = false
  }

  // 이름 검증
  if (!form.name.trim()) {
    errors.name = '이름을 입력해주세요.'
    isValid = false
  } else if (form.name.length < 2) {
    errors.name = '이름은 2자 이상이어야 합니다.'
    isValid = false
  }

  // 전화번호 검증
  if (!form.phone) {
    errors.phone = '전화번호를 입력해주세요.'
    isValid = false
  } else if (!/^01(?:0|1|[6-9])-(?:\d{3}|\d{4})-\d{4}$/.test(form.phone)) {
    errors.phone = '올바른 전화번호 형식을 입력해주세요. (예: 010-1234-5678)'
    isValid = false
  }

  return isValid
}

// 회원가입 처리
const handleSubmit = async () => {
  if (!validateForm()) return

  try {
    const userData = {
      email: form.email,
      password: form.password,
      name: form.name,
      phone: form.phone
    }

    const success = await authStore.register(userData)
    if (success) {
      toast.success('회원가입이 완료되었습니다!')
      router.push('/login')
    }
  } catch (error: any) {
    if (error.response?.status === 409) {
      errors.email = '이미 등록된 이메일입니다.'
    } else if (error.response?.status === 400) {
      errors.general = '입력 정보를 확인해주세요.'
    } else {
      errors.general = '회원가입 중 오류가 발생했습니다. 다시 시도해주세요.'
    }
  }
}
</script>